import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleTable } from "@/core/components/simple-table";
import { FINAL_PAYOUT_KEY } from "@/icm/constants";
import type { EnrichedIcmStatement } from "@/icm/types";
import { getTableMinWidth } from "@/icm/utils";
import { getPayBreakdownStatementPath } from "@/payroll/pay-breakdown/routes";
import {
  BreakdownCol,
  PlanNameCol,
  StatementTotalPayoutCol,
  TechnicianNameCol,
} from "./cols";

interface IcmTechnicianBreakdownTableProps {
  periodStart?: string;
  statements: EnrichedIcmStatement[];
  searchValue: string;
  setSearchValue: (value: string) => void;
}

export const IcmTechnicianBreakdownTable = ({
  periodStart,
  statements,
  searchValue,
  setSearchValue,
}: IcmTechnicianBreakdownTableProps) => {
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const showPlan =
      new Set(
        statements.map((statement) => statement.plan_name).filter(Boolean),
      ).size > 1;
    const breakdownItems = Array.from(
      new Map(
        statements.flatMap((statement) =>
          statement.payout_breakdown_items
            .filter((item) => item.key !== FINAL_PAYOUT_KEY)
            .map((item) => [item.key, item]),
        ),
      ).values(),
    );

    return [
      TechnicianNameCol,
      ...(showPlan ? [PlanNameCol] : []),
      ...breakdownItems.map(BreakdownCol),
      StatementTotalPayoutCol,
    ];
  }, [statements]);

  const tableMinWidth = useMemo(() => getTableMinWidth(columns), [columns]);

  return (
    <SimpleTable
      data={statements}
      columns={columns}
      columnPinning={{
        left: ["technician_name"],
        right: ["total_payout"],
      }}
      onRowClick={(statement) =>
        navigate(
          getPayBreakdownStatementPath(
            periodStart ?? statement.period_start,
            statement.id,
          ),
        )
      }
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      increaseHeaderPadding
      scrollAreaHeightClassName="h-[58vh]"
      scrollAreaClassName="pr-0"
      scrollContentStyle={{ minWidth: tableMinWidth }}
    />
  );
};
